import * as Currencies from "@dinero.js/currencies";
import Decimal from "decimal.js";
import {
  add,
  Calculator,
  createDinero,
  Currency,
  Dinero,
  subtract,
  toDecimal,
} from "dinero.js";
import { isNil, memoize } from "lodash-es";
import { DataTableContext } from "shared/components/table/data/DataTableContext";
import { tryParseDecimal } from "shared/utils/DecimalUtils";
import { removeNonNumericCharacters } from "shared/utils/StringUtils";
import { isTruthy } from "shared/utils/utils";

type DecimalCurrency = Currency<Decimal>;

const calculator: Calculator<Decimal> = {
  add: (a, b) => a.add(b),
  compare: (a, b) => a.cmp(b),
  decrement: (v) => v.minus(new Decimal(1)),
  increment: (v) => v.plus(new Decimal(1)),
  integerDivide: (a, b) => a.dividedToIntegerBy(b),
  modulo: (a, b) => a.mod(b),
  multiply: (a, b) => a.times(b),
  power: (a, b) => a.pow(Number(b)),
  subtract: (a, b) => a.minus(b),
  zero: () => new Decimal(0),
};

const dineroDecimal = createDinero({
  calculator,
  onCreate: ({ amount }) => amount,
  formatter: {
    toString: (amount) => amount?.toFixed(0) ?? "",
    toNumber: (amount) => amount?.toNumber() ?? 0,
  },
});

const moneyNumberFormat = memoize(
  (currency: Currencies.Currency<Decimal>): Intl.NumberFormat =>
    new Intl.NumberFormat(undefined, moneyNumberFormatOptions(currency)),
);

const moneyNumberFormatOptions = (
  currency: Currencies.Currency<Decimal>,
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormatOptions => ({
  style: "currency",
  currencySign: "accounting",
  currency: currency.code,
  ...options,
});

const intlFormat = (
  dineroObject: Dinero<Decimal>,
  locale: Intl.LocalesArgument = "en-US",
  options: Intl.NumberFormatOptions = {},
) =>
  toDecimal(dineroObject, ({ value, currency }) =>
    Number(value).toLocaleString(
      locale,
      moneyNumberFormatOptions(currency, options),
    ),
  );
export const USD: DecimalCurrency = {
  code: "USD",
  base: new Decimal(10),
  exponent: new Decimal(2),
};

export const zeroMoney = (currency = USD) =>
  dineroDecimal({
    amount: new Decimal(0),
    currency,
  });

export const moneyFromString = (unformattedString: string, currency = USD) =>
  dineroDecimal({
    amount: new Decimal(unformattedString).times(100),
    currency,
  });

export const tryMoneyFromString = (
  unformattedString: string | null | undefined,
  currency = USD,
  defaultValue = zeroMoney(currency),
) => {
  if (!unformattedString) {
    return defaultValue;
  }
  try {
    return moneyFromString(unformattedString, currency);
  } catch {
    return defaultValue;
  }
};

export const moneyToString = (d: Dinero<Decimal>) => toDecimal(d);

export const formatMoney = <T extends string | null | undefined>(
  amount: T,
  currency = USD,
  locale = "en-US",
  options: Intl.NumberFormatOptions = {},
) =>
  (amount
    ? intlFormat(moneyFromString(amount, currency), locale, options)
    : // Type assertion needed because of typescript limitation, see:
      // https://github.com/microsoft/TypeScript/issues/33912
      undefined) as T extends string ? string : undefined;

export const formatMoneyToParts = (amount: number, currency = USD) =>
  moneyNumberFormat(currency).formatToParts(amount);

export const formatMoneyWithoutCurrencySymbol = (
  amount: string | null | undefined,
  currency = USD,
  fallback = "-",
) => {
  const amountAsNumber = Number(amount);
  return !isNaN(amountAsNumber)
    ? formatMoneyToParts(amountAsNumber, currency)
        .filter((p) => p.type !== "currency")
        .map((p) => p.value)
        .join("")
    : fallback;
};

export const symbolForCurrency = memoize(
  (currency: DecimalCurrency) =>
    formatMoneyToParts(0, currency).find(({ type }) => type === "currency")!
      .value,
);

export const sumMoneyStrings = (amounts: (string | null | undefined)[]) => {
  if (!amounts.some(isTruthy)) {
    return "";
  }
  return moneyToString(
    amounts
      .filter((amount) => amount && !!tryParseDecimal(amount))
      .map((amount) => tryMoneyFromString(amount))
      .reduce(add, zeroMoney()),
  );
};

export const negateMoneyString = (amount: string | null | undefined) =>
  moneyToString(subtract(zeroMoney(), tryMoneyFromString(amount)));

export const subtractMoneyStrings = (
  amount1: string | null | undefined,
  amount2: string | null | undefined,
) =>
  moneyToString(
    subtract(tryMoneyFromString(amount1), tryMoneyFromString(amount2)),
  );

export const divideMoneyStrings = (
  numerator: string | null | undefined,
  denominator: string | null | undefined,
) => {
  if (!denominator) {
    return formatMoney("0");
  }

  const numeratorAsDecimal = tryParseDecimal(numerator);
  const denominatorAsDecimal = tryParseDecimal(denominator);

  if (
    !numeratorAsDecimal ||
    !denominatorAsDecimal ||
    denominatorAsDecimal.eq(0)
  ) {
    return formatMoney("0");
  }

  return formatMoneyFromDecimal(
    numeratorAsDecimal.div(denominatorAsDecimal),
    USD,
    moneyNumberFormatOptions(USD, { maximumFractionDigits: 0 }),
  );
};

export const parseCurrency = (currencyCode = "USD") => {
  const currency = Currencies[currencyCode as keyof typeof Currencies];
  const decimalCurrency: DecimalCurrency = {
    code: currency.code,
    base: new Decimal(currency.base as number),
    exponent: new Decimal(currency.exponent),
  };
  return decimalCurrency;
};

export const moneyFromDecimal = (
  amount: Decimal,
  currency = USD,
): Dinero<Decimal> =>
  dineroDecimal({
    amount: amount.times(100),
    currency,
  });

export const formatMoneyFromDecimal = (
  amount: Decimal,
  currency = USD,
  options: Intl.NumberFormatOptions = {},
) => intlFormat(moneyFromDecimal(amount, currency), "en-US", options);

export const formatMoneyObject = (money: Dinero<Decimal>) => intlFormat(money);

export const formatMoneyStringForTable = (
  tableContext: DataTableContext,
  amount: string | null,
  currency = USD,
) => {
  if (!amount || !Number(amount)) {
    return "-";
  }
  return formatMoneyWithoutCurrencySymbol(amount, currency);
};

export const formatMoneyForTable = (amount: string | null | undefined) => {
  if (isNil(amount)) {
    return "";
  }
  const { sanitized, isNegative } = removeNonNumericCharacters(amount);

  const numericValue = isNegative ? `-${sanitized}` : sanitized;

  return tryParseDecimal(numericValue)?.toString() ?? "";
};
